import * as React from 'react';
import { addImageParams, getFinalCategoryIdFromPathname, getImageUrl } from '@/utils/urls';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getAuctioneerContextMessageIds,
    getFollowContextMessageIds,
    getItemContextMessageIds,
} from '@/utils/modalContextMessage';
import { getCatalogSellerId, getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCategory } from '@/redux/modules/category/category.selectors';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getModalContext } from '@/redux/modules/modal.selectors';
import { getPageLabel } from '@/redux/modules/landingPage';
import { getSearchedAndPromotedItems } from '@/redux/modules/search/aggregateResults/searchAggregate.selectors';
import { getSellerLogoUrl, getSellerName } from '@/redux/modules/seller';
import { parseQueryParams } from '@/utils/queryParams';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import useGetSearchColumns from '@/hooks/useGetSearchColumns';

interface ModalContextInfo {
    /** secondary text node after title */
    blurb?: React.ReactNode;
    /** catalog ID */
    catalogId?: number;
    /** email address */
    emailAddress?: string;
    /** header text node */
    header?: React.ReactNode;
    /** image alt text */
    imageAlt?: string;
    /** image url */
    imageUrl?: string;
    /** item ID */
    itemId?: number;
    /** image placeholder url */
    placeholderUrl?: string;
    /** arguments to pass to runAtLoginFunction */
    runAtLoginArgs?: any;
    /** if present, should run after login succeeds */
    runAtLoginFunction?: Function;
    /** seller ID */
    sellerId?: number;
    /** ModalContext.type */
    type: string;
}

const CREATOR_DB_ID = 4;

/**
 * useGetModalContextInfo
 * returns object with necessary information to display a contextual modal header
 * @returns ModalContextInfo
 */
const useGetModalContextInfo = (): ModalContextInfo => {
    const modalContext = useAppSelector(getModalContext);

    const modalContextInfo: ModalContextInfo = {
        blurb: '',
        catalogId: 0,
        emailAddress: modalContext?.emailAddress ?? '',
        header: '',
        imageAlt: '',
        imageUrl: '',
        itemId: 0,
        placeholderUrl: '',
        sellerId: 0,
        type: modalContext.type,
    };

    // determine context
    const isItemContext = [
        'see sold price',
        'see similar items',
        'save item',
        'place bid',
        'ask a question - item',
        'register for auction - item',
        'exit intent - item',
        'exit intent - price result',
    ].includes(modalContext.type);
    const isSellerContext = ['follow auctioneer', 'ask a question - auctioneer'].includes(modalContext.type);
    const isCatalogContext = modalContext.type === 'register for auction';
    const isSearchContext = ['exit intent - search', 'follow search'].includes(modalContext.type);
    // item context
    const itemSummary = useAppSelector((state) => getItemSummary(state, modalContext.id));
    // seller context
    const sellerName = useAppSelector((state) => getSellerName(state, modalContext.id));
    const sellerLogoUrl = useAppSelector((state) => getSellerLogoUrl(state, modalContext.id, 80));
    // catalog context
    const catalogSellerId = useAppSelector((state) => getCatalogSellerId(state, modalContext.id));
    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, modalContext.id));
    const catalogSellerName = useAppSelector((state) => getSellerName(state, catalogSellerId));
    const catalogSellerLogoUrl = useAppSelector((state) => getSellerLogoUrl(state, catalogSellerId, 80));
    // search context
    const columns = useGetSearchColumns();
    const searchPageItemIds = useAppSelector((state) => getSearchedAndPromotedItems(state, columns));
    const searchItem = useAppSelector((state) => getItemSummary(state, searchPageItemIds[0]));
    const location = useLocation();
    // exit intent context (on the hunt modal trigger)
    const { pathname } = location;
    const facets = useAppSelector((state) => getItemFacets(state, modalContext.id));
    const categoryId = Number(getFinalCategoryIdFromPathname(pathname));
    const category = useAppSelector((state) => getCategory(state, categoryId));
    const categoryPageLabel = useAppSelector(getPageLabel);
    let creatorName = '';
    if (itemSummary.itemId) {
        creatorName = facets?.creators?.[0]?.l1CategoryName || '';
    } else if (category.name && ((category.facetId === CREATOR_DB_ID && isItemContext) || !isItemContext)) {
        creatorName = categoryPageLabel;
    }

    if (isItemContext) {
        const { catalogId, imageVersion, itemId, photos, sellerId, title } = itemSummary;
        modalContextInfo.catalogId = catalogId;
        modalContextInfo.itemId = itemId;
        modalContextInfo.sellerId = sellerId;
        modalContextInfo.imageAlt = title || 'Item';
        modalContextInfo.imageUrl = getImageUrl({
            catalogId,
            imageNumber: photos[0],
            imageVersion,
            itemId,
            sellerId,
            width: 80,
        });
        const [header, blurb] = getItemContextMessageIds(modalContext.type);
        modalContextInfo.header = <FormattedMessage id={header} />;
        modalContextInfo.blurb = <FormattedMessage id={blurb} />;
    } else if (isSellerContext) {
        modalContextInfo.sellerId = modalContext.id;
        modalContextInfo.imageAlt = sellerName || 'Seller';
        modalContextInfo.imageUrl = sellerLogoUrl;
        modalContextInfo.placeholderUrl = addImageParams({
            quality: 15,
            url: sellerLogoUrl,
        });
        const [header, blurb] = getAuctioneerContextMessageIds(modalContext.type);
        modalContextInfo.header = <FormattedMessage id={header} />;
        modalContextInfo.blurb = (
            <FormattedMessage
                id={blurb}
                values={{ name: sellerName || 'this seller' }}
            />
        );
    } else if (isCatalogContext) {
        modalContextInfo.catalogId = modalContext.id;
        modalContextInfo.imageAlt = catalogTitle ? `${catalogSellerName} catalog ${catalogTitle}` : 'Catalog';
        modalContextInfo.imageUrl = catalogSellerLogoUrl;
        modalContextInfo.header = <FormattedMessage id="signUpModal.getApproved" />;
        modalContextInfo.blurb = <FormattedMessage id="signUpModal.registerApprove" />;
    } else if (isSearchContext) {
        if (searchItem) {
            modalContextInfo.imageAlt = 'Item';
            modalContextInfo.imageUrl = getImageUrl({
                catalogId: searchItem.catalogId,
                imageNumber: searchItem.photos[0],
                imageVersion: searchItem.imageVersion,
                itemId: searchItem.itemId,
                sellerId: searchItem.sellerId,
                width: 80,
            });
        }
        const queryParams: any = parseQueryParams(location.search);
        const searchTerm = queryParams.keyword || '';
        const [header, blurb] = getFollowContextMessageIds(modalContext.type);
        modalContextInfo.header = <FormattedMessage id={header} />;
        modalContextInfo.blurb = (
            <FormattedMessage
                id={blurb}
                values={{ name: searchTerm }}
            />
        );
    }

    if (modalContext.type?.includes('exit intent') || modalContext.type?.includes('see similar item')) {
        modalContextInfo.header = (
            <FormattedMessage
                id={creatorName ? 'onTheHuntModal.huntingFor' : 'onTheHuntModal.onTheHunt'}
                values={{ creatorName }}
            />
        );
        modalContextInfo.blurb = null;
    }

    if (Boolean(modalContext.runAtLoginFunction)) {
        modalContextInfo.runAtLoginFunction = modalContext.runAtLoginFunction;
        modalContextInfo.runAtLoginArgs = modalContext.runAtLoginArgs;
    }

    return modalContextInfo;
};

export default useGetModalContextInfo;
